import { AlertTriangleIcon } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  cn,
  Progress,
  toast,
  useConfirm,
  type BadgeProps,
} from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import { AuditLogPanel } from '@/shared/components/AuditLogPanel';
import { OffboardingDetailDtoStatus, type OffboardingDetailDto } from '@/api/generated/model';
import { useCompleteOffboardingMutation } from '../hooks/useOffboarding';
import { OffboardingChecklist } from './OffboardingChecklist';
import { TerminationPanel } from './TerminationPanel';
import { SettlementsPanel } from './SettlementsPanel';
import { NotificationsPanel } from './NotificationsPanel';
import { RetentionsPanel } from './RetentionsPanel';

const STATUS_VARIANT: Record<OffboardingDetailDtoStatus, BadgeProps['variant']> = {
  [OffboardingDetailDtoStatus.in_progress]: 'destructive',
  [OffboardingDetailDtoStatus.completed]: 'success',
  [OffboardingDetailDtoStatus.cancelled]: 'secondary',
};

export interface OffboardingPanelProps {
  offboarding: OffboardingDetailDto;
  /** Currency for the settlement figures — the player's, not a global default. */
  currency?: string;
  /** Hidden on the player page, where the header already names the player. */
  showPlayerName?: boolean;
}

/**
 * The whole offboarding body: header, four steps, audit trail.
 *
 * One component with two homes — the player page's Offboarding tab and the
 * standalone `/offboarding` screen both render this. Built once rather than
 * twice because the four steps are where every rule in this feature lives, and
 * two copies would mean two places for the locking rule and the honesty banners
 * to drift out of step.
 *
 * A completed or cancelled offboarding renders read-only throughout. It has
 * stopped being a worklist and become a record, and the API refuses the writes
 * anyway.
 */
export function OffboardingPanel({
  offboarding,
  currency = 'EUR',
  showPlayerName = true,
}: OffboardingPanelProps) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const complete = useCompleteOffboardingMutation();

  const closed = offboarding.status !== OffboardingDetailDtoStatus.in_progress;

  const completeOffboarding = () =>
    void confirm({
      title: t('offboarding.complete.confirm', { name: offboarding.player.fullName }),
      description: t('offboarding.complete.body'),
      confirmText: t('offboarding.complete.action'),
      cancelText: t('actions.cancel'),
      destructive: true,
      onConfirm: () =>
        complete.mutateAsync(offboarding.id).then(
          () => toast.success(t('offboarding.complete.done')),
          (error: unknown) => {
            toast.error(error instanceof Error ? error.message : t('offboarding.complete.failed'));
            throw error;
          },
        ),
    });

  return (
    <div className="grid gap-4">
      <Card className={cn('gap-3 px-4', !closed && 'border-destructive/40')}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-2.5">
            <AlertTriangleIcon className="mt-0.5 size-4 shrink-0 text-destructive" />
            <div className="grid gap-0.5">
              <h3 className="text-[15px] font-semibold">
                {showPlayerName
                  ? t('offboarding.title', { name: offboarding.player.fullName })
                  : t('offboarding.checklist.title')}
              </h3>
              <p className="text-[13px] text-muted-foreground">{t('offboarding.description')}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Badge variant={STATUS_VARIANT[offboarding.status]}>
              {t(`offboarding.state.${offboarding.status}`)}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {t('offboarding.stepsDone', {
                done: offboarding.completedTasks,
                total: offboarding.totalTasks,
              })}
            </span>
            {closed ? null : (
              <Button
                variant="destructive"
                size="sm"
                // `canComplete` is the API's answer, not ours — it accounts for
                // the status as well as the step count. The endpoint 409s
                // regardless, so this is a hint rather than the enforcement.
                disabled={!offboarding.canComplete}
                loading={complete.isPending}
                title={offboarding.canComplete ? undefined : t('offboarding.complete.blocked')}
                onClick={completeOffboarding}
              >
                {t('offboarding.complete.action')}
              </Button>
            )}
          </div>
        </div>

        <Progress
          value={offboarding.completionPercent}
          indicatorClassName={closed ? 'bg-success' : 'bg-destructive'}
        />
      </Card>

      <OffboardingChecklist
        offboardingId={offboarding.id}
        tasks={offboarding.tasks}
        readOnly={closed}
      />

      <TerminationPanel offboarding={offboarding} readOnly={closed} />
      <SettlementsPanel offboarding={offboarding} currency={currency} readOnly={closed} />
      <NotificationsPanel offboarding={offboarding} readOnly={closed} />
      <RetentionsPanel offboarding={offboarding} readOnly={closed} />

      <AuditLogPanel
        subjectType="player_offboarding"
        subjectId={offboarding.id}
        i18nPrefix="offboarding.audit"
      />
    </div>
  );
}
